<template>
  <PreGameModal :progress="progress" :progressTotal="progressTotal">
    <template #content>
      <div class="content">
        <div class="header">
          <div class="title">{{ $t("game.preGameModalQuestions.title") }}</div>
          <div class="subtitle">{{ $t("game.preGameModalQuestions.subtitle") }}</div>
        </div>
        <Transition name="fade" mode="out-in">
          <div class="question" :key="step">
            <div class="questionCount">{{ $t("game.preGameModalQuestions.questionLabel", { n: step, m: questions.length }) }}</div>
            <div class="questionText">{{ questions[step - 1].question }}</div>
            <div class="questionOptions">
              <div v-for="(option, index) in questions[step - 1].options" :key="index" class="option" :class="{ selected: selectedAnswers[step - 1] === option }" @click="selectAnswer(step - 1, option)">
                <input type="radio" :name="'question' + step" :value="option" :id="'option' + index" :checked="selectedAnswers[step - 1] === option" />
                <label :for="'option' + index">{{ option }}</label>
              </div>
            </div>
          </div>
        </Transition>
        <div class="buttons">
          <Button :label="$t('game.preGameModalQuestions.previousButton')" class="btn secondary outline" @click="Previous" v-if="step >= 2" />
          <Button :label="step >= questions.length ? $t('game.preGameModalQuestions.validateButton') : $t('game.preGameModalQuestions.nextButton')" class="btn primary" @click="Next" :disabled="!selectedAnswers[step - 1]" />
        </div>
      </div>
    </template>
  </PreGameModal>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.content {
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;

  line-height: normal;

  gap: 40px;

  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .title {
      font-size: 32px;
      font-weight: 700;

      @media screen and (max-width: $small-screen) {
        font-size: 24px;
      }
    }

    .subtitle {
      font-size: 18px;

      @media screen and (max-width: $small-screen) {
        font-size: 14px;
      }
    }
  }

  .question {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    min-height: 250px;

    .questionCount {
      color: #f2cb36;
      font-size: 16px;
      font-weight: 700;

      @media screen and (max-width: $small-screen) {
        font-size: 12px;
      }
    }

    .questionText {
      font-size: 28px;
      font-weight: 700;
      text-align: center;

      @media screen and (max-width: $small-screen) {
        font-size: 18px;
      }
    }

    .questionOptions {
      margin-top: 16px;

      display: flex;
      flex-wrap: wrap;
      gap: 16px;

      @media screen and (max-width: $small-screen) {
        gap: 8px;
      }

      .option {
        // width: 33%;

        display: flex;
        padding: 16px 24px;
        align-items: center;
        gap: 8px;
        flex: 0 0 30%;

        @media screen and (max-width: $small-screen) {
          flex-basis: 100%;
          font-size: 13px;
          padding: 14px 20px;
        }

        cursor: pointer;

        // apply cursor pointer to all childs
        * {
          cursor: pointer;
        }

        border-radius: 6px;
        border: 2px solid #464646;
        transition: all 0.2s ease;

        &.selected {
          border-color: #f2cb36;
          background-color: #f2cb361a;
        }

        &:hover {
          border-color: #f2cb36;
        }

        input[type="radio"] {
          flex: 0 0 auto;
        }
      }
    }
  }

  .buttons {
    display: flex;
    gap: 16px;

    .btn {
      border: 2px solid #464646 !important;
    }
  }
}
</style>

<script setup lang="ts">
import { ref, onMounted } from "vue";

import { useCloudClientStore } from "@/stores/cloud-client";
import PreGameModal from "./PreGameModal.vue";

const cloudClient = useCloudClientStore();
const assessmentStore = useAssessmentStore();
const { tm } = useI18n();

const route = useRoute();

const questions = tm("game.preGameModalQuestions.questions");

defineProps({
  progress: {
    type: Number,
    required: true,
  },
  progressTotal: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["next"]);

// Start at question number 1
const step = ref(1);

// Store user answers
const selectedAnswers = ref<Array<string>>([]);

// Select an answer for a question
function selectAnswer(questionIndex: number, option: string) {
  selectedAnswers.value[questionIndex] = option;
}

function Next() {
  if (step.value >= questions.length) {
    cloudClient.pushSurvey(selectedAnswers.value);
    localStorage.setItem("selectedAnswers", JSON.stringify(selectedAnswers.value));
    emit("next");
  }
  step.value++;
}

function Previous() {
  if (step.value <= 1) return;
  step.value--;
}

onMounted(() => {
  const savedAnswers = localStorage.getItem("selectedAnswers");
  const assessmentId = route.query.assessmentId;
  const assessment = assessmentStore?.assessments.find((a) => a?.campaign_id === assessmentId);
  if (!assessment?.campaign?.surveys?.demographic) emit("next");
  else if (savedAnswers) emit("next");
});
</script>
